import gql from 'graphql-tag';
import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { DigitalExhibitQuizFragment } from "@graphqlTypes";
import className from "classnames/bind";
import { useQuizMachine } from "./useQuizMachine";
import { useQuizIntro } from "./useQuizIntro";
import { useQuizDeepLink } from "./useQuizDeepLink";
import { useHideConsentBadge } from "./useHideConsentBadge";
import QuizMedia from "./QuizMedia";
import QuizProgress from "./QuizProgress";
import QuizLanding from "./QuizLanding";
import QuizQuestion from "./QuizQuestion";
import QuizResults from "./QuizResults";
import type { QuizQuestionData, QuizResultLevel, QuizResult } from "./types";
import { QuizFragment } from '@/fragments/DigitalExhibit';
import { accentVar } from "../utils";

import styles from "./DigitalExhibitQuiz.module.scss";
const cx = className.bind(styles);

type DigitalExhibitQuizProps = Omit<DigitalExhibitQuizFragment, "__typename"> & {
  isDefault?: boolean;
  textCase?: string;
};

export default function DigitalExhibitQuiz({ quiz, accent, isDefault }: DigitalExhibitQuizProps) {
  const node = quiz?.nodes?.[0];
  const isQuiz = node?.__typename === "Quiz" && !!node.quizFields;
  const quizFields = (isQuiz ? node.quizFields : null) ?? {};

  const {
    eyebrow,
    title,
    supportingCopy,
    ctaLabel,
    knockoutEnabled,
    knockoutColor,
    dimImagery,
    imageMode,
    image,
    questions,
    resultLevels,
    imageAttribution,
    quizType,
    results,
    resultsImageMode,
    resultsImage,
    resultsImageAttribution,
    enableShare,
  } = quizFields;

  const questionList = (questions ?? []).filter(Boolean) as QuizQuestionData[];
  const resultLevelList = (resultLevels ?? []).filter(Boolean) as QuizResultLevel[];
  const landingImage = image?.node ?? null;
  const resultList = (results ?? []).filter(Boolean) as QuizResult[];

  // Reserve panel height for the question with the most options so the module
  // never clips a longer list and never jumps between questions.
  const maxOptions = Math.max(2, ...questionList.map((q) => (q.options ?? []).filter(Boolean).length));

  const machine = useQuizMachine(questionList, resultLevelList, quizType, resultList);
  const { started, phase, current, answers, canAdvance, isPersonality, personalityResult } = machine;
  const { rootRef, stageRef, mediaRef, imgRef, panelRef, lockupRef, reduceMotion, handleBegin, handleNext, handleBack, handleRetake } =
    useQuizIntro(machine, Boolean(dimImagery));

  // Derived, not a ref: recomputed every render from the state it depends on,
  // so QuizMedia gets a fresh value whenever `started`/`current` change.
  const activeResultsImage = isPersonality
    ? (resultsImageMode === "multi" ? personalityResult?.image?.node : resultsImage?.node) ?? null
    : null;

  // Gated on activeResultsImage: an attribution without its own image would
  // credit whatever image the media slot falls through to instead.
  const resultsAttribution = activeResultsImage
    ? (isPersonality
        ? (resultsImageMode === "multi" ? personalityResult?.imageAttribution : resultsImageAttribution) ?? null
        : null)
    : null;

  const currentAttribution =
    phase === "results" && resultsAttribution
      ? resultsAttribution
      : started
        ? questionList[current]?.imageAttribution ?? null
        : imageAttribution ?? null;

  // Default theme has no page-level module deep-link handler; resolve our own.
  useQuizDeepLink(rootRef, isDefault, reduceMotion);

  useHideConsentBadge(rootRef);

  // Returning to the landing (Back on Q1, Retake) unmounts the button the
  // keyboard user just pressed — hand focus to the landing title so the
  // screen reader re-announces the quiz and Tab reaches Start next. Gated on
  // wasStarted so the initial page render never steals focus. Runs in a
  // passive effect, after useQuizIntro's layout effect has un-inerted the
  // landing lockup.
  const landingHeadingRef = useRef<HTMLHeadingElement>(null);
  const wasStartedRef = useRef(false);

  useEffect(() => {
    if (started) {
      wasStartedRef.current = true;
      return;
    }
    if (wasStartedRef.current) landingHeadingRef.current?.focus({ preventScroll: true });
  }, [started]);

  if (!isQuiz) return null;

  return (
    <section
      id="dx-quiz"
      ref={rootRef}
      aria-label={title || "Quiz"}
      style={{
        "--dx-quiz-max-options": maxOptions,
        "--highlight-color": accent ? accentVar(accent) : undefined,
      } as CSSProperties}
      className={cx("dx-quiz", { "dx-quiz--default": isDefault, "is-started": started, "dx-quiz--static": reduceMotion })}
    >
      {/* Media + panel share one stage */}
      <div ref={stageRef} className={cx("dx-quiz__stage")}>
        <div ref={panelRef} className={cx("dx-quiz__panel")}>
          {phase === "quiz" && (
            <QuizProgress
              label={machine.progressLabel}
              pct={machine.progressPct}
              announce={`Question ${current + 1} of ${questionList.length}`}
            />
          )}

          {phase === "quiz" && questionList[current] && (
            <QuizQuestion
              question={questionList[current]}
              current={current}
              started={started}
              answers={answers}
              canAdvance={canAdvance}
              select={machine.select}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {phase === "results" && (
            <QuizResults
              title={title}
              score={machine.score}
              total={questionList.length}
              result={isPersonality ? personalityResult : machine.resultTier}
              isPersonality={isPersonality}
              showShare={!isPersonality || enableShare !== false}
              onRetake={handleRetake}
            />
          )}
        </div>

        {/* Landing hero overlay — inerted/faded once started (see useQuizIntro) */}
        <div ref={lockupRef} className={cx("dx-quiz__lockup")}>
          <QuizLanding
            eyebrow={eyebrow}
            title={title}
            supportingCopy={supportingCopy}
            ctaLabel={ctaLabel}
            knockoutEnabled={knockoutEnabled}
            knockoutColor={knockoutColor}
            onBegin={handleBegin}
            headingRef={landingHeadingRef}
          />
        </div>
        <QuizMedia
          imageMode={imageMode}
          landingImage={landingImage}
          questions={questionList}
          current={current}
          started={started}
          phase={phase}
          resultsImage={activeResultsImage}
          dimImagery={dimImagery}
          mediaRef={mediaRef}
          imageAttribution={currentAttribution}
          imgRef={imgRef}
        />
      </div>
    </section>
  );
}

// ----------------------------------------
// GraphQL Fragments for use outside of DX context see DigitalExhibit.ts for DX usage.
// ----------------------------------------
DigitalExhibitQuiz.fragments = {
  entry: gql`
    ${QuizFragment}
    fragment FlexibleLayoutQuiz on FlexiblePageContentContentQuizLayout {
      quiz {
        nodes {
          ...QuizFragment
        }
      }
    }
  `,
  inducteeEntry: gql`
    ${QuizFragment}
    fragment ImpactQuiz on InducteeDataImpactFlexibleContentQuizLayout {
      quiz {
        nodes {
          ...QuizFragment
        }
      }
    }
  `,
};